import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useListenEvent } from "go-core/hooks";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import BarcodeScanner from "../../../../../../../../../../../../components/Product/BarcodeScanner/BarcodeScanner";
import {
	ProductApi,
	ProductStateApi,
	PurchaseApi,
	PurchaseItemApi,
	TaxApi,
	TaxItemApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";
import ChangeProductModal from "../../../../../../components/ChangeProductModal";
import { getRecalculatedSummaryTaxes } from "../../../../../../utils/summaryTaxesUtils";
import PurchaseFormItemsSummaryTable from "../SummaryTable/PurchaseFormItemsSummaryTable";
import PurchaseFormItemsSummaryTableButtons from "../SummaryTable/PurchaseFormItemsSummaryTableButtons";
import PurchaseFormItemsTableActions from "./PurchaseFormItemsTableActions";
import PurchaseFormItemsTableBody from "./PurchaseFormItemsTableBody";
import PurchaseFormItemsTableHeader from "./PurchaseFormItemsTableHeader";

interface Props {
	form: UseFormReturn<PurchaseApi>;
	taxes: TaxApi[];
	purchase?: PurchaseApi;
	fetchPurchase?: () => void;
	getExternalStates: (items: PurchaseItemApi[]) => Promise<void>;
	externalProductStates: ProductStateApi[];
	getExternalStateName: () => string;
}

const PurchaseFormItemsTable: FC<Props> = ({
	form,
	taxes,
	purchase,
	fetchPurchase,
	getExternalStates,
	externalProductStates,
	getExternalStateName,
}) => {
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const { control, register, clearErrors, watch, setValue } = form;
	const watchedItems = watch("items");
	const optionsRef = useRef<{ label: string }[]>([]);
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const [showDeviationFromReferencePrice, setShowDeviationFromReferencePrice] = useState<boolean>(false);
	const [productStates, setProductStates] = useState<ProductStateApi[]>([]);
	const [summaryTaxes, setSummaryTaxes] = useState<TaxItemApi[]>([]);
	const watchedProductIds = watchedItems.map((item) => item.product_id).filter((id) => id);
	const watchedStockId = watch("stock_id");
	const watchedCurrency = watch("currency");
	const watchedProductIdsAsString = JSON.stringify(watchedProductIds);
	const [exchangeRateLoading, setExchangeRateLoading] = useState<boolean>(false);
	const [exchangeRate, setExchangeRate] = useState<number>(purchase?.exchange_rate || 1);
	const firstRender = useRef<boolean>(true);
	const [showChangeProductModal, setShowChangeProductModal] = useState<number | null>(null);
	const itemsAsString = JSON.stringify(watchedItems);
	const [showBarcodeScanner, setShowBarcodeScanner] = useState<boolean>(false);
	const { isMobile } = useWindowSize();

	const handleOpenBarcodeScanner = useCallback((e) => {
		if (e.key === "Enter") {
			if (!showBarcodeScanner) setShowBarcodeScanner(true);
		}
	}, []);

	useListenEvent({
		eventName: "keypress",
		callback: (e?: KeyboardEvent) => {
			if (!e) {
				return;
			}
			handleOpenBarcodeScanner(e);
		},
	});

	useEffect(() => {
		onUpdateFields();
	}, [fields]);

	useEffect(() => {
		if (!firstRender.current) onExchangeCurrencyRate();
	}, [watchedCurrency]);

	useEffect(() => {
		onUpdateFields();
		firstRender.current = false;
	}, []);

	useEffect(() => {
		if (watch("stock_id")) {
			onFetchProductStates();
		}
	}, [watchedStockId, watchedProductIdsAsString]);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];
		return (
			prevItem?.product_id ||
			(!isNaN(prevItem?.total_price_net_money?.amount) && prevItem.total_price_net_money?.amount !== null) ||
			(!isNaN(prevItem?.price_net_money?.amount) && prevItem.price_net_money?.amount !== null) ||
			(!isNaN(prevItem?.total_price_gross_money?.amount) && prevItem.total_price_gross_money?.amount !== null) ||
			(!isNaN(prevItem?.price_gross_money?.amount) && prevItem.price_gross_money?.amount !== null)
		);
	};

	useEffect(() => {
		if (checkIfAddNewItem())
			append({
				position: watchedItems.length - 1,
				id: new Date().getTime(),
				product_id: null,
				tax_id: null,
				product: null,
				volume: null,
				quantity: null,
				quantity_received: null,
				price_net_money: null,
				price_gross_money: null,
				total_price_net_money: null,
				total_price_gross_money: null,
				default_price_net_money: null,
				tax: null,
				comment: "",
				last_price: null,
				discounted: false,
			} as unknown as PurchaseItemApi);
	}, [itemsAsString]);

	const onFetchProductStates = async () => {
		try {
			const params = {
				include: "volumes",
				stock_id: watch("stock_id"),
				product_ids: watchedProductIds.join(","),
			};
			const res = await api.organization().getProductState(params);
			setProductStates(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onUpdateFields = () => {
		fields.forEach((field, index) => {
			registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
			setValue(`items.${index}.position`, index);
			setValue(`items.${index}.tax`, field.tax);
			setValue(`items.${index}.tax_id`, field.tax?.id);
			registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id"]);
			setValue(`items.${index}.product`, field?.product);
			setValue(`items.${index}.product_id`, field?.product?.id);
			setValue(`items.${index}.discounted`, field?.discounted !== undefined ? field.discounted : false);
		});
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	const fetchProductLastPrice = async (item: PurchaseItemApi, index: number) => {
		if (item.product_id && item.volume) {
			setTimeout(async () => {
				try {
					const res = await api.organization().getProductLastPrice(Number(item?.product_id), item.volume);
					if (!res || Object.keys(res).length === 0) {
						setValue(`items.${index}.price_net_money.amount`, 0);
						setValue(`items.${index}.total_price_net_money.amount`, 0);
					} else {
						setValue(`items.${index}.price_net_money.amount`, res.amount);
						const totalPriceNetMoneyAmount = !item.quantity ? 0 : res.amount * item.quantity;
						setValue(`items.${index}.total_price_net_money.amount`, totalPriceNetMoneyAmount);
					}
					setValue(`items.${index}.last_price`, res?.amount);
				} catch (err) {
					handleError.alert(err, addFlash);
				}
			}, 10);
		} else {
			setValue(`items.${index}.price_net_money.amount`, 0);
			setValue(`items.${index}.total_price_net_money.amount`, 0);
			setValue(`items.${index}.last_price`, 0);
		}
	};

	const onExchangeCurrencyRate = async () => {
		setExchangeRateLoading(true);
		try {
			const date = watch("purchased_at");
			const currency = watch("currency");
			const res = await api.organization().exchangeCurrencyRate(currency, date);
			setExchangeRate(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setExchangeRateLoading(false);
	};

	const onSearchProducts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"productType|e": `BASIC${FILTER_VALUE_SEPARATOR}COMPOSITE`,
			include: "volumes_v2",
		};
		return api.organization().getProductSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const onParseOptionValue = (option: { label: string }) => {
		optionsRef.current = optionsRef.current.find((o) => o.label === option.label)
			? [...optionsRef.current]
			: [...optionsRef.current, option];
		return option.label;
	};

	const [indexToFocus, setIndexToFocus] = useState(-1);
	const onChangeProduct = async (
		product: ProductApi,
		index: number,
		createdProduct?: { label: string; value: string }
	) => {
		if (createdProduct === null) return;

		setValue(`items.${index}.id`, undefined);
		clearErrors(`items.${index}.volume`);

		if (createdProduct) {
			setValue(`items.${index}.external_product_name`, createdProduct.label);
		} else {
			const tax = taxes.find((x) => x.id === product.tax_id) as TaxApi;
			registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id"]);
			setValue(`items.${index}.product`, product);
			setValue(`items.${index}.product_id`, product.id, { shouldDirty: true });
			setValue(`items.${index}.volume`, product?.default_volume ?? null, { shouldDirty: true });
			await fetchProductLastPrice(watch(`items.${index}`), index);
			registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
			setValue(`items.${index}.tax`, tax);
			setValue(`items.${index}.tax_id`, product?.tax_id ?? null, { shouldDirty: true });
			recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
		}

		setIndexToFocus(index);
		const volumeInput: HTMLInputElement | null = document.querySelector(`input[name='items.${index}.volume']`);
		if (volumeInput) {
			volumeInput.focus();
		}
		getExternalStates(watch("items"));
	};

	const recalculateSummaryTaxes = (
		items: PurchaseItemApi[],
		taxGross: boolean,
		index?: number,
		val?: number,
		quantity?: string,
		isTotalPrice?: boolean
	) => {
		if (index !== undefined) {
			clearErrors(`items.${index}.total_price_net_money`);
		}

		const newTaxes = getRecalculatedSummaryTaxes(items, taxGross, isTotalPrice);
		setSummaryTaxes(newTaxes);
	};

	const onConfirmChangeProduct = async (product: ProductApi) => {
		if (showChangeProductModal !== null) {
			await onChangeProduct(product, showChangeProductModal);
			setShowChangeProductModal(null);
		}
	};

	return (
		<fieldset className="form-group">
			<div className="d-flex align-items-center justify-content-between">
				<h5 className={isMobile ? "mb-0" : ""}>{t("common.word.items")}</h5>
				<PurchaseFormItemsTableActions
					showBarcodeScanner={showBarcodeScanner}
					setShowBarcodeScanner={setShowBarcodeScanner}
					setShowDeviationFromReferencePrice={setShowDeviationFromReferencePrice}
					showDeviationFromReferencePrice={showDeviationFromReferencePrice}
					watchedItems={watchedItems}
					form={form}
					remove={remove}
					append={append}
					replace={replace}
					recalculateSummaryTaxes={recalculateSummaryTaxes}
					fetchProductLastPrice={fetchProductLastPrice}
				/>
			</div>
			<div className={isMobile ? "table-responsive" : ""}>
				<table
					className={`table table-form positions-table positions-table--small ${
						isMobile ? "vertical-top" : ""
					}`}
				>
					<PurchaseFormItemsTableHeader
						watchedItems={watchedItems}
						shownIndexes={shownIndexes}
						setShownIndexes={setShownIndexes}
						fields={fields}
					/>
					<PurchaseFormItemsTableBody
						watchedItems={watchedItems}
						fields={fields}
						shownIndexes={shownIndexes}
						form={form}
						setShownIndexes={setShownIndexes}
						onChangeProduct={onChangeProduct}
						onParseOptionValue={onParseOptionValue}
						searchProducts={onSearchProducts}
						productStates={productStates}
						setShowChangeProductModal={setShowChangeProductModal}
						showDeviationFromReferencePrice={showDeviationFromReferencePrice}
						remove={remove}
						indexToFocus={indexToFocus}
						setIndexToFocus={setIndexToFocus}
						fetchProductLastPrice={fetchProductLastPrice}
						recalculateSummaryTaxes={recalculateSummaryTaxes}
						fetchPurchase={fetchPurchase}
						externalProductStates={externalProductStates}
						getExternalStateName={getExternalStateName}
					/>
				</table>
			</div>
			<div className="row positions-table-summary">
				<div className="col-md-6" />
				<div className="col col-md-6">
					<div className="d-flex align-items-center">
						<h6 className="mb-0">{t("lib:common.word.summary")}</h6>
						<PurchaseFormItemsSummaryTableButtons
							form={form}
							watchedItems={watchedItems}
							recalculateSummaryTaxes={recalculateSummaryTaxes}
						/>
					</div>
					<PurchaseFormItemsSummaryTable
						summaryTaxes={summaryTaxes}
						exchangeRateLoading={exchangeRateLoading}
						form={form}
						exchangeRate={exchangeRate}
					/>
				</div>
			</div>
			{showChangeProductModal !== null && (
				<ChangeProductModal
					isShown={showChangeProductModal !== null}
					onHide={() => setShowChangeProductModal(null)}
					onConfirm={onConfirmChangeProduct}
					onSearchProducts={onSearchProducts}
					index={showChangeProductModal}
					form={form}
					onParseOptionValue={onParseOptionValue}
				/>
			)}
			{showBarcodeScanner && (
				<BarcodeScanner
					isShown={showBarcodeScanner}
					organization={organization}
					onHide={() => setShowBarcodeScanner(false)}
					form={form}
					taxes={taxes}
				/>
			)}
		</fieldset>
	);
};

export default PurchaseFormItemsTable;
